/**
 * Nutrition Summary Modal - Shows nutrition comparison and bundle suggestions after dog update
 */

import React from "react";
import {
  Box,
  Button,
  Dialog,
  Typography,
  Stack,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PetsIcon from "@mui/icons-material/Pets";
import type { NutritionRecalculationResult } from "../../types";
import { ComparisonCard } from "./ComparisonCard";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  open: boolean;
  result: NutritionRecalculationResult | null;
  onClose: () => void;
  onGoToSubscriptions?: () => void;
}

export function NutritionSummaryModal({
  open,
  result,
  onClose,
  onGoToSubscriptions,
}: Props) {
  if (!result) return null;

  const { dog, previousNutrition, newNutrition, comparison } = result;
  const hasSignificantChange = comparison?.hasSignificantChange || false;

  const handleGoToSubscriptions = () => {
    onClose();
    onGoToSubscriptions?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        zIndex: 1400,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "16px" },
          bgcolor: QUIZ.color.warmWhite,
          maxHeight: { xs: "100%", sm: "90vh" },
          height: { xs: "100%", sm: "auto" },
          m: { xs: 0, sm: 3 },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
        },
      }}
    >
      {/* Header - Blue gradient */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #E6F0FF 0%, #D4E5FF 100%)",
          p: { xs: 2, md: 3 },
          flexShrink: 0,
          position: "relative",
          borderBottom: "1px solid #B3D4FF",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            bgcolor: "rgba(255,255,255,0.9)",
            "&:hover": { bgcolor: QUIZ.color.warmWhite },
          }}
        >
          <CloseIcon sx={{ color: "#1A57D3" }} />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "12px",
              bgcolor: "#1A57D3",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(26, 87, 211, 0.3)",
            }}
          >
            <PetsIcon sx={{ color: QUIZ.color.white, fontSize: 26 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                color: "#1A3A5C",
                fontWeight: 800,
                fontSize: { xs: 18, md: 22 },
                fontFamily: QUIZ.font.display,
              }}
            >
              {dog.name}'s Nutrition {previousNutrition ? "Updated!" : "Calculated!"}
            </Typography>
            <Typography
              sx={{ color: "#4A6A8A", fontSize: 13, mt: 0.25 }}
            >
              {previousNutrition
                ? "We've recalculated based on the new information"
                : "Here's the recommended nutrition plan"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: "auto", bgcolor: "#FAFCFF" }}>
        <Box
          sx={{
            maxWidth: 500,
            mx: "auto",
            p: { xs: 2, md: 3 },
          }}
        >
          <Stack spacing={3}>
            {/* Comparison Section */}
            {previousNutrition && comparison && (
              <Box>
                <Typography
                  sx={{
                    color: "#1A3A5C",
                    fontWeight: 800,
                    fontSize: 16,
                    mb: 2,
                  }}
                >
                  Nutrition Comparison
                </Typography>
                <ComparisonCard
                  previous={previousNutrition}
                  current={newNutrition}
                  comparison={comparison}
                />
                {hasSignificantChange && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "#E6F0FF",
                      borderRadius: "12px",
                      border: "1px solid #B3D4FF",
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1A57D3" }}>
                      Significant change detected!
                    </Typography>
                    <Typography sx={{ fontSize: 13, mt: 0.5, color: "#4A6A8A" }}>
                      Your dog's nutrition needs have changed by more than 10%.
                      Consider updating your subscription to match the new requirements.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* New Nutrition Summary */}
            <Box
              sx={{
                p: 3,
                bgcolor: QUIZ.color.warmWhite,
                borderRadius: "12px",
                border: "2px solid #E8EEF5",
              }}
            >
              <Typography
                sx={{
                  color: "#1A3A5C",
                  fontWeight: 800,
                  fontSize: 16,
                  mb: 1,
                }}
              >
                {dog.name}'s Updated Needs
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "baseline",
                  gap: 0.5,
                  bgcolor: "#1A57D3",
                  color: QUIZ.color.white,
                  px: 2,
                  py: 1,
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(26, 87, 211, 0.3)",
                }}
              >
                <Typography sx={{ fontSize: 24, fontWeight: 900 }}>
                  {newNutrition.gramsPerDay.toFixed(0)}g
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  per day
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 13,
                  color: "#4A6A8A",
                  mt: 1.5,
                }}
              >
                {newNutrition.caloriesPerDay.toFixed(0)} kcal daily requirement
              </Typography>
            </Box>

            {/* Suggestion to edit meals */}
            {onGoToSubscriptions && (
              <Box
                sx={{
                  p: 2.5,
                  bgcolor: "#E6F0FF",
                  borderRadius: "12px",
                  border: "1px solid #B3D4FF",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#1A3A5C",
                    mb: 1,
                  }}
                >
                  Want to update your meal plan to match {dog.name}'s new needs?
                </Typography>
                <Typography
                  onClick={handleGoToSubscriptions}
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#1A57D3",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Go to Subscriptions to edit your meals →
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Sticky Footer */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          bgcolor: QUIZ.color.warmWhite,
          borderTop: "1px solid #E8EEF5",
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            py: 1.5,
            px: 4,
            borderRadius: "10px",
            bgcolor: "#1A57D3",
            color: QUIZ.color.white,
            fontWeight: 700,
            fontSize: 14,
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(26, 87, 211, 0.3)",
            "&:hover": {
              bgcolor: "#1448B3",
            },
          }}
        >
          Done
        </Button>
      </Box>
    </Dialog>
  );
}
